import type { BpmnElement, Shape } from "bpmn-js/lib/model/Types";
import { getBusinessObject, is } from "bpmn-js/lib/util/ModelUtil";
import type { OverlayAttrs } from "diagram-js/lib/features/overlays/Overlays";
import { computed, h, onMounted, onUnmounted, ref, render, watch } from "vue";
import type { Ref, VNode } from "vue";
import { useRoute, useRouter } from "vue-router";

import LayerIcon from "@/components/BPMN/LayerIcon.vue";
import ProcessElement from "@/components/BPMN/ProcessElement.vue";
import RoleSelector from "@/components/BPMN/RoleSelector.vue";
import { ProcessTask } from "@/lib/openapi/gen";
import { useProcessElementsManager } from "@/stores/process-elements-store.js";
import { debounce, vnodeToHtml } from "@/utils";

import { useEditManager } from "../edit-manager";
import {
    applyIconHoverStyles,
    removeIconHoverStyles,
} from "./custom-modules/stekz-renderer/icon-hover-styles";
import {
    HIGH_PRIORITY,
    canHaveContent,
    elementIsLane,
    elementIsNavigatable,
    elementIsSubProcessShape,
    elementIsTask,
    elementType,
    getNavigationRoute,
} from "./helpers";
import type { useBpmnManager } from "./index";

const getCompliancyColor = () =>
    window
        .getComputedStyle(document.body)
        .getPropertyValue("--stekz-compliancy-color")
        .trim();

const getEntityRefColor = () =>
    window
        .getComputedStyle(document.body)
        .getPropertyValue("--stekz-linkedentities-color")
        .trim();

const getDocumentsColor = () =>
    window
        .getComputedStyle(document.body)
        .getPropertyValue("--stekz-documents-color")
        .trim();

const getDescriptionColor = () =>
    window
        .getComputedStyle(document.body)
        .getPropertyValue("--stekz-description-color")
        .trim();

interface LayerDefinition {
    label: string;
    type: string;
    userToggable?: boolean;
    color?: string;
    icon?: string | string[];
    placement: Placement;
    /**
     * The VNode is ONLY created ont he first pass and never recreated.
     * If the VNode must be reactive to any properties, make sure the properties
     * itself are reactive. E.g. by providing a `Ref` or fetcher instead of the
     * value directly.
     */
    vnode({ element }: { element: BpmnElement }): VNode | undefined;
    calculatePosition?({ element }: { element: BpmnElement }): Position;
    forceRewrite?(): boolean;
    css?({ element }: { element: BpmnElement }): string;
    condition?({
        element,
        selected,
    }: {
        element: BpmnElement;
        selected: boolean;
    }): boolean;
    onStateChange?: ({
        hover,
        select,
        modifying,
        elementId,
        overlayId,
    }: {
        elementId: string;
        overlayId: string;
        hover?: boolean;
        select?: boolean;
        modifying?: boolean;
    }) => void;
    initialState?: { hover?: boolean; select?: boolean; modifying?: boolean };
}

type Position = OverlayAttrs["position"];
type Placement =
    | "TopLeft"
    | "TopCenter"
    | "TopRight"
    | "BottomLeft"
    | "BottomCenter"
    | "BottomRight"
    | "Center";

/**
 * Persist user-toggable layer visibility across sessions. Only the layers that
 * users can toggle from the legend popover are read from / written to storage;
 * internal layers (highlight, navigationalElement, etc.) are not user-controlled
 * so we don't store their state.
 */
const LAYER_VISIBILITY_STORAGE_KEY = "bpmn.layerVisibility";
const PERSISTED_LAYER_TYPES = [
    "linkedEntities",
    "compliancy",
    "documents",
    "description",
];

const defaultLayerVisibility: Record<string, boolean> = {
    navigationalIcon: true,
    navigationalElement: true,
    taskWindowElement: false,
    roleSelect: true,
    linkedEntities: true,
    highlight: true,
    compliancy: true,
    documents: true,
    description: true,
};

const loadPersistedVisibility = (): Record<string, boolean> => {
    const merged = { ...defaultLayerVisibility };
    try {
        const raw = localStorage.getItem(LAYER_VISIBILITY_STORAGE_KEY);
        if (!raw) return merged;
        const stored = JSON.parse(raw) as Record<string, unknown>;
        for (const type of PERSISTED_LAYER_TYPES) {
            if (typeof stored[type] === "boolean") {
                merged[type] = stored[type] as boolean;
            }
        }
    } catch {
        // Ignore corrupted storage; fall back to defaults.
    }
    return merged;
};

const layerVisibility: Ref<Record<string, boolean>> = ref(
    loadPersistedVisibility()
);

const persistLayerVisibility = () => {
    try {
        const toStore: Record<string, boolean> = {};
        for (const type of PERSISTED_LAYER_TYPES) {
            toStore[type] = layerVisibility.value[type] ?? true;
        }
        localStorage.setItem(
            LAYER_VISIBILITY_STORAGE_KEY,
            JSON.stringify(toStore)
        );
    } catch {
        // Storage may be unavailable (private mode, quota); ignore.
    }
};

interface NavigationalElementCallbacks {
    onHoveringNavigationalElement: (elementId: string | null) => void;
}

interface Features {
    navigationalElements: {
        enabled: Readonly<Ref<boolean>>;
        callbacks?: NavigationalElementCallbacks;
    };
    taskWindowElements: {
        enabled: Readonly<Ref<boolean>>;
    };
    roleSelect: {
        enabled: Readonly<Ref<boolean>>;
    };
    linkedEntities: {
        enabled: Readonly<Ref<boolean>>;
    };
    compliancy?: {
        enabled: Readonly<Ref<boolean>>;
    };
    documents?: {
        enabled: Readonly<Ref<boolean>>;
    };
    description?: {
        enabled: Readonly<Ref<boolean>>;
    };
    highlight: {
        enabled: Readonly<Ref<"onHover" | "onExternal" | "onAll" | false>>;
        highlightSeletor?: (element: BpmnElement) => boolean;
    };
    hoveringPopup: {
        enabled: Readonly<Ref<boolean>>;
    };
}

export function useBpmnLayerManager(
    bpmnManager: ReturnType<typeof useBpmnManager>,
    features?: Features
) {
    const isMounted = ref(false);
    const { isEditing } = useEditManager();
    const { processElements } = useProcessElementsManager();
    const router = useRouter();
    const route = useRoute();

    /** Per-element hide timeout for the navigational icon overlay. */
    const navIconHideTimeouts: Record<string, NodeJS.Timeout | null> = {};

    const definitions = [
        {
            label: "NavigationalIcon",
            type: "navigationalIcon",
            placement: "TopRight",
            vnode: ({ element }) =>
                h(
                    "div",
                    {
                        class: "navigational-icon-button",
                        onClick: (event: MouseEvent) => {
                            event.stopPropagation();
                            if (elementIsSubProcessShape(element)) {
                                bpmnManager.selectSubProcess(element);
                            } else {
                                const navigationRoute = getNavigationRoute(
                                    element,
                                    route.params.orgName as string,
                                    route.params.stekName as string
                                );
                                if (navigationRoute) {
                                    router.push(navigationRoute);
                                    return;
                                }

                                // Fallback for elements matched via bpmn_id (not yet
                                // embedded with processElementId in the BPMN XML).
                                const bo = getBusinessObject(element);
                                const pe = processElements.value?.find(
                                    (p) => p.bpmn_id === bo.id
                                );
                                const elType = pe && elementType(element);
                                if (pe && elType) {
                                    router.push({
                                        name: "processElementPage",
                                        params: {
                                            orgName: route.params.orgName,
                                            stekName: route.params.stekName,
                                            elementId: pe.identity,
                                            elementType: elType,
                                        },
                                    });
                                }
                            }
                        },
                        onMouseenter: (event: MouseEvent) => {
                            // Cancel any pending hide — the mouse is on the icon.
                            if (navIconHideTimeouts[element.id]) {
                                clearTimeout(navIconHideTimeouts[element.id]);
                                navIconHideTimeouts[element.id] = null;
                            }
                            bpmnManager.eventBus?.fire("element.hover", {
                                element,
                                gfx: bpmnManager.elementRegistry?.getGraphics(
                                    element
                                ),
                                originalEvent: event,
                                type: undefined,
                            });
                        },
                        onMouseleave: () => {
                            // Directly hide the overlay container rather than firing
                            // element.out on the event bus. Firing element.out triggers
                            // bpmn-js internal handlers (e.g. DirectEditing) that can
                            // crash when the element's gfx is undefined during label
                            // editing completion.
                            const overlays =
                                bpmnManager.bpmnJs.value?.get("overlays");
                            if (!overlays) return;
                            const overlayResult = overlays.get({
                                element,
                                type: "navigationalIcon",
                            });
                            const overlayList =
                                overlayResult instanceof Array
                                    ? overlayResult
                                    : [overlayResult];
                            const overlay = overlayList[0] as
                                | (OverlayAttrs & {
                                      htmlContainer: HTMLElement;
                                  })
                                | undefined;
                            if (!overlay?.htmlContainer) return;
                            navIconHideTimeouts[element.id] = setTimeout(() => {
                                overlay.htmlContainer.classList.remove(
                                    "is-hovered"
                                );
                                navIconHideTimeouts[element.id] = null;
                            }, 200);
                        },
                    },
                    h("i", {
                        class: elementIsSubProcessShape(element)
                            ? "pi pi-window-maximize"
                            : "pi pi-arrow-up-right",
                    })
                ),
            condition: ({ element }) => {
                if (elementIsNavigatable(element)) return true;
                // Fallback: match elements that exist in the processElements store by
                // bpmn_id but don't yet have processElementId embedded in the BPMN XML.
                const bo = getBusinessObject(element);
                return (
                    canHaveContent(element) &&
                    !!processElements.value?.find((pe) => pe.bpmn_id === bo.id)
                );
            },
            onStateChange: ({ hover, overlayId, elementId }) => {
                if (hover === undefined) return;

                const overlays = bpmnManager.bpmnJs.value?.get("overlays");
                const overlay = overlays?.get(overlayId) as
                    | (OverlayAttrs & { htmlContainer: HTMLElement })
                    | undefined;
                if (!overlay?.htmlContainer) return;

                if (hover) {
                    if (navIconHideTimeouts[elementId]) {
                        clearTimeout(navIconHideTimeouts[elementId]);
                        navIconHideTimeouts[elementId] = null;
                    }
                    overlay.htmlContainer.classList.add("is-hovered");
                } else {
                    navIconHideTimeouts[elementId] = setTimeout(() => {
                        overlay.htmlContainer.classList.remove("is-hovered");
                        navIconHideTimeouts[elementId] = null;
                    }, 200);
                }
            },
        },
    ] as LayerDefinition[];

    function getLayerDefinitions() {
        const layerDefinitions: LayerDefinition[] = [...definitions];

        if (!features) {
            return layerDefinitions;
        }

        if (features.navigationalElements?.enabled?.value) {
            layerDefinitions.push({
                label: "NavigationalElement",
                type: "navigationalElement",
                placement: "Center",
                vnode: ({ element }) => {
                    return h(ProcessElement, {
                        element,
                        elementType: elementType(element),
                        bpmnManager,
                        isHoverPopupEnabled:
                            features.hoveringPopup?.enabled.value,
                    });
                },
                // The ProcessElement is always 16px larger than the element (8px padding on all sides).
                // So we can hardcode the position to be -8px from the top left corner.
                calculatePosition: () => ({ left: -8, top: -8 }),
                forceRewrite: () => true,
                condition: ({ element }) => {
                    const bo = getBusinessObject(element);

                    // Skip expanded pools as they are just containers and would
                    // block interaction with elements inside them.
                    if (is(element, "bpmn:Participant") && !element.collapsed) {
                        return false;
                    }

                    if (
                        bo.processElementId ||
                        bo.processId ||
                        bo.calledElementId
                    ) {
                        return true;
                    }

                    // Fallback: a task/event/gateway that exists in the processElements
                    // store (by bpmn_id) is navigatable even before its processElementId
                    // has been embedded into the BPMN XML (e.g. immediately after sync).
                    return (
                        canHaveContent(element) &&
                        !!processElements.value?.find(
                            (pe) => pe.bpmn_id === bo.id
                        )
                    );
                },
                css: ({ element }) => {
                    // Note, so far, below only works for task-like elements. This should be extended once we support
                    // more types of navigational elements. Using :hover is much faster, [data-hover] is for when
                    // hover is triggered through the highlight function.
                    const gElement = `g[data-element-id="${element.id}"]`;

                    if (isEditing.value) {
                        return "";
                    }

                    return `
                        ${gElement} * {
                            cursor: pointer !important;
                        }
                    `;
                },
                onStateChange: ({ elementId, hover }) => {
                    if (!bpmnManager.elementRegistry) return;
                    if (hover === true) {
                        features.navigationalElements.callbacks?.onHoveringNavigationalElement(
                            elementId
                        );
                    } else if (hover === false) {
                        features.navigationalElements.callbacks?.onHoveringNavigationalElement(
                            null
                        );
                    }
                },
            });
        }

        if (features.highlight?.enabled?.value) {
            layerDefinitions.push({
                label: "Highlight",
                type: "highlight",
                placement: "TopLeft",
                vnode: () => h("div", { class: "highlight" }),
                css: ({ element }) => {
                    const gElement = `g[data-element-id="${element.id}"]`;
                    // The :not([data-stekz-fill]) guard scopes the dark fill to the
                    // main bounding shape only. StekzRenderer marks every icon/marker
                    // primitive with data-stekz-fill, so excluding those leaves icons
                    // free to be recolored to white by applyIconHoverStyles below.
                    const externalHighlight = `
                        /* Element. */
                        ${gElement}[data-hover] g.djs-visual circle:not([data-stekz-fill]),
                        ${gElement}[data-hover] g.djs-visual polygon:not([data-stekz-fill]),
                        ${gElement}[data-hover] g.djs-visual rect:not([data-stekz-fill]) {
                            fill: var(--stekz-primary-color) !important;
                        }

                        /* Text inside elements. */
                        ${gElement}[data-hover] g.djs-visual text {
                            fill: white !important;
                        }

                    `;

                    const hoverHighlight = `
                        /* Element. */
                        ${gElement}:hover g.djs-visual rect:not([data-stekz-fill]),
                        ${gElement}:hover g.djs-visual circle:not([data-stekz-fill]),
                        ${gElement}:hover g.djs-visual polygon:not([data-stekz-fill]) {
                            fill: var(--stekz-primary-color) !important;
                        }

                        /* Text inside elements. */
                        ${gElement}:hover g.djs-visual text {
                            fill: white !important;
                        }
                    `;

                    if (features.highlight.enabled.value === "onExternal") {
                        return externalHighlight;
                    } else {
                        return `
                            ${externalHighlight}
                            ${hoverHighlight}
                        `;
                    }
                },
                condition: ({ element }) =>
                    features.highlight.highlightSeletor
                        ? features.highlight.highlightSeletor(element)
                        : !is(element, "bpmn:Collaboration"),
                onStateChange: ({ elementId, hover, select }) => {
                    if (!bpmnManager.elementRegistry) return;
                    const graphics =
                        bpmnManager.elementRegistry.getGraphics(elementId);
                    if (!graphics) return;

                    if (
                        features.highlight.enabled.value === "onHover" ||
                        features.highlight.enabled.value === "onAll"
                    ) {
                        if (hover === true) {
                            graphics.dataset.hover = "";
                        } else if (hover === false) {
                            delete graphics.dataset.hover;
                        }
                    }

                    if (
                        features.highlight.enabled.value === "onExternal" ||
                        features.highlight.enabled.value === "onAll"
                    ) {
                        if (select === true) {
                            graphics.dataset.hover = "";
                        } else if (select === false) {
                            delete graphics.dataset.hover;
                        }
                    }

                    // Sync icon colors with [data-hover] state using JS so that
                    // the renderer's CSS-variable inline styles are properly overridden.
                    // Use a stekzHovered marker to only call removeIconHoverStyles when
                    // transitioning away from hover — not on initial (hover: false) fires.
                    const visuals =
                        graphics.querySelector<SVGElement>(".djs-visual");
                    if (visuals) {
                        if (graphics.dataset.hover !== undefined) {
                            graphics.dataset.stekzHovered = "";
                            applyIconHoverStyles(visuals);
                        } else if ("stekzHovered" in graphics.dataset) {
                            delete graphics.dataset.stekzHovered;
                            removeIconHoverStyles(visuals);
                        }
                    }
                },
            });
        }

        if (features.taskWindowElements?.enabled?.value) {
            const icon = h("i", {
                class: "pi pi-folder-plus inline-task-page",
            });

            layerDefinitions.push({
                label: "TaskWindowElement",
                type: "taskWindowElement",
                placement: "TopCenter",
                vnode: () => icon,
                condition: ({ element, selected }) =>
                    canHaveContent(element) && selected,
            });
        }

        if (features.roleSelect?.enabled?.value) {
            layerDefinitions.push({
                label: "roleSelect",
                type: "roleSelect",
                placement: "TopLeft",
                vnode: ({ element }) => {
                    return h(RoleSelector, { element, bpmnManager });
                },
                condition: ({ element }) => elementIsLane(element),
            });
        }

        if (features.linkedEntities?.enabled?.value) {
            layerDefinitions.push({
                label: "Domain Objects",
                type: "linkedEntities",
                userToggable: true,
                color: getEntityRefColor(),
                icon: ["fas", "building"],
                placement: "BottomLeft",
                vnode: ({ element }) => {
                    const bo = getBusinessObject(element);
                    return h(LayerIcon, {
                        color: getEntityRefColor(),
                        icon: ["fas", "building"],
                        elementId: bo.id,
                        processElementId: bo.processElementId,
                        mode: "linkedEntities",
                    });
                },
                css() {
                    return `.djs-overlay-linkedEntities { pointer-events: none; }`;
                },
                condition: ({ element }) => {
                    const bo = getBusinessObject(element);
                    const pe = processElements.value?.find(
                        (p) =>
                            p.identity === bo.processElementId ||
                            p.bpmn_id === bo.id
                    );
                    return (pe?.entity_refs?.length ?? 0) > 0;
                },
            });
        }

        if (features.compliancy?.enabled?.value) {
            layerDefinitions.push({
                label: "Compliancy",
                type: "compliancy",
                userToggable: true,
                color: getCompliancyColor(),
                icon: ["fas", "shield"],
                placement: "BottomLeft",
                vnode: ({ element }) => {
                    const bo = getBusinessObject(element);
                    return h(LayerIcon, {
                        color: getCompliancyColor(),
                        icon: ["fas", "shield"],
                        elementId: bo.id,
                        processElementId: bo.processElementId,
                        mode: "compliancy",
                    });
                },
                css() {
                    return `.djs-overlay-compliancy { pointer-events: none; }`;
                },
                condition: ({ element }) => {
                    if (!elementIsTask(element)) return false;
                    const bo = getBusinessObject(element);
                    const pe = processElements.value?.find(
                        (p) =>
                            p.identity === bo.processElementId ||
                            p.bpmn_id === bo.id
                    );
                    return (pe as ProcessTask)?.generates_evidence === true;
                },
            });
        }

        if (features.documents?.enabled?.value) {
            layerDefinitions.push({
                label: "Documents",
                type: "documents",
                userToggable: true,
                color: getDocumentsColor(),
                icon: ["fas", "file"],
                placement: "BottomLeft",
                vnode: ({ element }) => {
                    const bo = getBusinessObject(element);
                    return h(LayerIcon, {
                        color: getDocumentsColor(),
                        icon: ["fas", "file"],
                        elementId: bo.id,
                        processElementId: bo.processElementId,
                        mode: "documents",
                    });
                },
                css() {
                    return `.djs-overlay-documents { pointer-events: none; }`;
                },
                condition: ({ element }) => {
                    if (!elementIsTask(element)) return false;
                    const bo = getBusinessObject(element);
                    const pe = processElements.value?.find(
                        (p) =>
                            p.identity === bo.processElementId ||
                            p.bpmn_id === bo.id
                    );
                    return ((pe as ProcessTask)?.document_count ?? 0) > 0;
                },
            });
        }

        if (features.description?.enabled?.value) {
            layerDefinitions.push({
                label: "Description",
                type: "description",
                userToggable: true,
                color: getDescriptionColor(),
                icon: ["fas", "align-left"],
                placement: "BottomLeft",
                vnode: ({ element }) => {
                    const bo = getBusinessObject(element);
                    return h(LayerIcon, {
                        color: getDescriptionColor(),
                        icon: ["fas", "align-left"],
                        elementId: bo.id,
                        processElementId: bo.processElementId,
                        mode: "description",
                    });
                },
                css() {
                    return `.djs-overlay-description { pointer-events: none; }`;
                },
                condition: ({ element }) => {
                    if (!elementIsTask(element)) return false;
                    const bo = getBusinessObject(element);
                    const pe = processElements.value?.find(
                        (p) =>
                            p.identity === bo.processElementId ||
                            p.bpmn_id === bo.id
                    );
                    return Boolean(pe?.short_description);
                },
            });
        }

        return layerDefinitions;
    }

    const getHtmlId = (element: BpmnElement, definition: LayerDefinition) => {
        return `${element.businessObject.id}_${definition.type}`;
    };

    const htmls: Record<string, HTMLElement> = {};
    /**
     * Get HTML from overlayDefinition for given element and state
     */
    const getHtml = (
        element: BpmnElement,
        definition: LayerDefinition
    ): HTMLElement | undefined => {
        const id = getHtmlId(element, definition);

        const vnode = definition.vnode({ element });

        if (!vnode) return;

        // Generate HTML if not yet exists.
        if (!(id in htmls) || definition.forceRewrite?.()) {
            htmls[id] = vnodeToHtml(vnode);
        }
        return htmls[id];
    };

    /**
     * Set visibility for overlay.
     */
    const setLayerVisibility = (type: string, visible: boolean) => {
        layerVisibility.value[type] = visible;
        if (PERSISTED_LAYER_TYPES.includes(type)) {
            persistLayerVisibility();
        }
        // Re-run update() so peers in the same placement re-flow: hiding
        // routes through overlaysToBeRemoved, and showing creates fresh
        // overlays — both paths recalculate stacking positions from scratch.
        update();
    };

    /**
     * Get the width and height for a given element, scale factored in.
     */
    const getElementSize = (
        htmlElement: HTMLElement
    ): { width: number; height: number } => {
        const viewbox = bpmnManager.canvas?.viewbox();
        const { width, height } = htmlElement.getBoundingClientRect();
        if (viewbox?.scale) {
            return {
                width: width / viewbox.scale,
                height: height / viewbox.scale,
            };
        } else {
            return { width, height };
        }
    };

    /**
     * Some elements have thick lines which we do not want to have overlap
     * with when showing an icon. To account for this, we return an offset which
     * can be used to position the overlay.
     */
    const getElementOffset = (
        element: BpmnElement
    ): { widthOffset: number; heightOffset: number } => {
        let widthOffset = 0;
        let heightOffset = 0;

        if (elementIsTask(element)) {
            widthOffset = 1;
            heightOffset = 1;
        }

        const viewbox = bpmnManager.canvas?.viewbox();
        if (viewbox?.scale) {
            return {
                widthOffset: widthOffset / viewbox.scale,
                heightOffset: heightOffset / viewbox.scale,
            };
        } else {
            return { widthOffset, heightOffset };
        }
    };

    /**
     * Get the initial position for an element in the layer.
     * The element MUST be drawn already before calling this method.
     */
    const getInitialPosition = (
        element: BpmnElement,
        htmlElement: HTMLElement,
        placement: Placement = "BottomLeft"
    ): Position => {
        const { width: widthWithoutOffset, height: heightWithoutOffset } =
            getElementSize(htmlElement);
        const { widthOffset, heightOffset } = getElementOffset(element);
        const width = widthWithoutOffset + widthOffset;
        const height = heightWithoutOffset + heightOffset;
        const bounds = element.di?.bounds;

        if (placement === "BottomLeft") {
            if (bounds) {
                return {
                    left: 0,
                    top: bounds.height - height,
                };
            }
            return { left: 0, bottom: 0 };
        } else if (placement === "TopLeft") {
            if (bounds) {
                return {
                    left: 0,
                    top: 0,
                };
            }
            return { left: 0, top: 0 };
        } else if (placement === "TopCenter") {
            if (bounds) {
                return {
                    left: bounds.width / 2 - width / 2,
                    top: 0,
                };
            }
            return { left: width / 2, top: 0 };
        } else if (placement === "TopRight") {
            if (bounds) {
                return {
                    left: bounds.width - width,
                    top: 0,
                };
            }
            return { right: 0, top: 0 };
        } else if (placement === "BottomRight") {
            if (bounds) {
                return {
                    left: bounds.width - width,
                    top: bounds.height - height,
                };
            }
            return { right: 0, bottom: 0 };
        } else if (placement === "BottomCenter") {
            if (bounds) {
                return {
                    left: bounds.width / 2 - width / 2,
                    top: bounds.height - height,
                };
            }
            return { left: width / 2, bottom: 0 };
        } else if (placement === "Center") {
            if (bounds) {
                return {
                    left: bounds.width / 2 - width / 2,
                    top: bounds.height / 2 - height / 2,
                };
            }
            return { left: 0, top: 0 };
        }
        throw Error("Doesn't support given combination of parameters.");
    };

    /**
     * Get the position for the next element in the layer.
     */
    const getNextPosition = (
        lastPosition: Position,
        currentElement: HTMLElement,
        direction: "right" = "right"
    ): Position => {
        const { width } = getElementSize(currentElement);
        if (direction !== "right") {
            throw Error("Currently only supports 'right' as direction.");
        }
        return { ...lastPosition, left: (lastPosition.left || 0) + width };
    };

    interface StateOverlays {
        [elementId: string]: {
            [overlayId: string]: LayerDefinition;
        };
    }
    const overlaysListeningToState: Ref<StateOverlays> = ref({});

    /** Stores the currently highlighted element ID so highlight can be re-applied after update(). */
    let _currentHighlightId: string | null = null;

    /** Apply highlight styling to an element's overlays (if they exist). */
    const applyHighlight = (elementId: string) => {
        if (elementId in overlaysListeningToState.value) {
            Object.entries(overlaysListeningToState.value[elementId]).forEach(
                ([overlayId, definition]) => {
                    definition.onStateChange?.({
                        select: true,
                        elementId,
                        overlayId,
                    });
                }
            );
        }
    };

    /** Remove highlight styling from an element's overlays (if they exist). */
    const removeHighlight = (elementId: string) => {
        if (elementId in overlaysListeningToState.value) {
            Object.entries(overlaysListeningToState.value[elementId]).forEach(
                ([overlayId, definition]) => {
                    definition.onStateChange?.({
                        select: false,
                        elementId,
                        overlayId,
                    });
                }
            );
        }
    };

    /**
     * Add all overlays as defined in layerDefinitions
     */
    const update = () => {
        // Check if the component is still mounted.
        if (!isMounted.value) return;

        // Ensure the manager is initialized and we have an element registry to work with.
        if (
            !bpmnManager.initialized.value ||
            !bpmnManager.elementRegistry ||
            !bpmnManager.bpmnJs.value
        ) {
            return;
        }

        const overlays = bpmnManager.bpmnJs.value.get("overlays");
        if (!overlays) return;

        let customCss = "";
        try {
            bpmnManager.elementRegistry.forEach((element) => {
                const positionsByPlacement: Partial<
                    Record<Placement, Position>
                > = {};
                const bpmnElement = element as BpmnElement;
                if (!bpmnElement.di) return;

                const layerDefinitions = getLayerDefinitions();
                const expectedOverlays = layerDefinitions
                    .filter((layer) => {
                        const result =
                            layer.condition?.({
                                element: bpmnElement,
                                selected: Boolean(
                                    bpmnManager.selection
                                        ?.get()
                                        .includes(bpmnElement)
                                ),
                            }) && !element.type.toLowerCase().includes("label");
                        if (!result) {
                            const id = getHtmlId(bpmnElement, layer);
                            delete htmls[id];
                        }
                        return result;
                    })
                    .filter(({ type }) => {
                        return layerVisibility.value[type];
                    });

                const currentOverlays = layerDefinitions.flatMap(
                    ({ type }) =>
                        overlays.get({ element: bpmnElement, type }) || []
                );

                const overlaysToBeRemoved = currentOverlays.filter(
                    (overlay) =>
                        !expectedOverlays.find(
                            (definition) =>
                                definition.type === overlay.type &&
                                overlay.element === element
                        )
                );

                expectedOverlays.forEach((definition) => {
                    const htmlElement = getHtml(bpmnElement, definition);
                    if (!htmlElement) {
                        return;
                    }

                    let overlayId = currentOverlays.find(
                        (overlay) => overlay.type === definition.type
                    )?.id;

                    // Add the overlay if it doesn't exist. We'll update the position later.
                    if (!overlayId) {
                        overlayId = overlays.add(bpmnElement, definition.type, {
                            position: {},
                            html: htmlElement,
                        });
                    }

                    let position: Position;
                    if (definition.calculatePosition) {
                        position = definition.calculatePosition({
                            element: bpmnElement,
                        });
                    } else {
                        position =
                            positionsByPlacement[definition.placement] ??
                            getInitialPosition(
                                bpmnElement,
                                htmlElement,
                                definition.placement
                            );
                    }

                    // Note, this hidden function updates the position
                    // of the overlay without having to recreate it. This way
                    // we can re-use overlay ids and keep things fast.
                    const existingOverlay = overlays.get(overlayId);
                    if (existingOverlay) {
                        // @ts-expect-error 2339
                        overlays._updateOverlay({
                            ...existingOverlay,
                            position,
                        });
                    }

                    if (definition.onStateChange) {
                        if (!overlaysListeningToState.value[element.id]) {
                            overlaysListeningToState.value[element.id] = {};
                        }

                        overlaysListeningToState.value[element.id][overlayId] =
                            definition;

                        // Trigger any given "initialState".
                        if (definition.initialState) {
                            definition.onStateChange({
                                ...definition.initialState,
                                elementId: element.id,
                                overlayId: overlayId,
                            });
                        }
                    }

                    if (definition.css) {
                        customCss += definition
                            .css({ element: bpmnElement })
                            .trim();
                    }

                    positionsByPlacement[definition.placement] =
                        getNextPosition(position, htmlElement);
                });

                overlaysToBeRemoved.forEach((overlay) => {
                    delete overlaysListeningToState.value[element.id];
                    overlays.remove({ id: overlay.id });
                });
            });
        } catch (e) {
            // This can happen when the diagram is being destroyed or re-imported.
            console.debug("Error while updating overlays", e);
        }

        bpmnManager.injectStyles(customCss);

        // Re-apply the stored highlight after overlays have been (re)created.
        if (_currentHighlightId) {
            applyHighlight(_currentHighlightId);
        }

        // Apply sticky positioning to newly (re)created role selector overlays.
        updateRoleSelectorStickyPositions();
    };

    /**
     * Keep the RoleSelector overlay pinned to the viewport's left edge when a lane
     * is wider than the visible area. As the user pans horizontally and the lane's
     * left edge scrolls off-screen, the role label stays visible inside the lane.
     * Translation is clamped so the selector never leaves the lane to the right.
     */
    const updateRoleSelectorStickyPositions = () => {
        if (!features?.roleSelect?.enabled?.value) return;
        if (!bpmnManager.elementRegistry || !bpmnManager.canvas) return;
        const overlays = bpmnManager.bpmnJs.value?.get("overlays");
        if (!overlays) return;
        const viewbox = bpmnManager.canvas.viewbox();
        if (!viewbox) return;

        bpmnManager.elementRegistry.forEach((element) => {
            const bpmnElement = element as BpmnElement;
            if (!elementIsLane(bpmnElement) || !bpmnElement.di?.bounds) return;

            const overlayResult = overlays.get({
                element: bpmnElement,
                type: "roleSelect",
            });
            const overlay = (
                Array.isArray(overlayResult) ? overlayResult[0] : overlayResult
            ) as (OverlayAttrs & { htmlContainer?: HTMLElement }) | undefined;
            if (!overlay?.htmlContainer) return;

            const laneX = bpmnElement.di.bounds.x;
            const laneWidth = bpmnElement.di.bounds.width;
            // The RoleSelector is position:absolute inside the overlay, so the
            // overlay wrapper collapses to 0 width. Measure the selector itself
            // to get its rendered width in BPMN units (offsetWidth is pre-scale).
            const selectorEl =
                overlay.htmlContainer.querySelector<HTMLElement>(
                    ".role-selector"
                );
            const selectorWidth = selectorEl?.offsetWidth ?? 0;

            // Positive when the lane's left edge is past the viewport's left edge
            // and the selector should slide right to stay visible.
            const overflow = viewbox.x - laneX;
            // Clamp so the selector's right edge never passes the lane's right edge.
            const maxSlide = laneWidth - selectorWidth;

            if (overflow > 0 && maxSlide > 0) {
                const translation = Math.min(overflow, maxSlide);
                overlay.htmlContainer.style.transform = `translateX(${translation}px)`;
            } else {
                overlay.htmlContainer.style.transform = "";
            }
        });
    };

    // Using debounce, because bpmnManager.elementRegistry.forEach internally
    // uses 'var' to point to a specific element in a map, which is an issue
    // when update is called multiple times in the same timespan, because
    // 'var' might be updated while the forEach loop is still running for the
    // 'other' update. Something something hoisting.
    const DEBOUNCE_MS = 10;

    /**
     * Add all overlays as defined in layerDefinitions
     */
    const attach = async () => {
        if (!bpmnManager.initialized) {
            console.warn(
                "You've attached this too early. First initialize a modeler or viewer."
            );
        }
        update();
        bpmnManager.bpmnJs.value!.on("import.done", () => {
            // Defer until after the browser has painted the BPMN elements, so
            // getInitialPosition can read correct DOM dimensions.
            setTimeout(() => {
                update();
                // Execute any state changes for the default state.
                Object.entries(overlaysListeningToState.value).forEach(
                    ([elementId, overlays]) => {
                        Object.entries(overlays).forEach(
                            ([overlayId, definition]) => {
                                definition.onStateChange?.({
                                    elementId: elementId,
                                    modifying: false,
                                    hover: false,
                                    overlayId,
                                });
                            }
                        );
                    }
                );
            }, DEBOUNCE_MS);
        });

        // Keep the RoleSelector pinned to the viewport's left edge as the user pans.
        bpmnManager.eventBus!.on(
            "canvas.viewbox.changed",
            updateRoleSelectorStickyPositions
        );

        // Re-create overlays when element properties change (e.g. processElementId
        // being set after import.done). Debounced to avoid excessive updates during
        // bulk property changes or editing.
        const debouncedUpdate = debounce(update, 50);
        bpmnManager.eventBus!.on("commandStack.changed", debouncedUpdate);

        /**
         * Properly unmount Vue components from overlays before they are removed from the DOM.
         */
        bpmnManager.eventBus!.on<{ context: { shape: Shape } }>(
            "commandStack.shape.delete.preExecute",
            HIGH_PRIORITY,
            ({ context }) => {
                const overlays = bpmnManager.bpmnJs.value?.get("overlays");
                let overlay = overlays!.get({ element: context.shape.id });
                if (Symbol.iterator in overlay) {
                    overlay = overlay[0];
                }
                if (!overlay) return;
                // Properly trigger unmounting of the Vue elements (if any);
                try {
                    render(null, overlay.html as HTMLElement);
                } catch (e) {
                    console.debug("Could not unmount, due to: " + e);
                }
            }
        );

        bpmnManager.eventBus!.on("element.click", () => {
            update();
            // Set ALL overlays to modifying false.
            Object.entries(overlaysListeningToState.value).forEach(
                ([elementId, overlays]) => {
                    Object.entries(overlays).forEach(
                        ([overlayId, definition]) => {
                            definition.onStateChange?.({
                                elementId: elementId,
                                modifying: false,
                                overlayId,
                            });
                        }
                    );
                }
            );
        });

        bpmnManager.eventBus!.on(
            "element.dblclick",
            ({ element }: Event & { element: BpmnElement }) => {
                // Set overlays for current element to modifying true.
                if (element.id in overlaysListeningToState.value) {
                    Object.entries(
                        overlaysListeningToState.value[element.id]
                    ).forEach(([overlayId, definition]) => {
                        definition.onStateChange?.({
                            elementId: element.id,
                            modifying: true,
                            overlayId,
                        });
                    });
                }
            }
        );

        bpmnManager.eventBus!.on(
            "element.hover",
            ({ element }: Event & { element: BpmnElement }) => {
                // Set overlays for current element to hover true.
                if (element.id in overlaysListeningToState.value) {
                    Object.entries(
                        overlaysListeningToState.value[element.id]
                    ).forEach(([overlayId, definition]) => {
                        definition.onStateChange?.({
                            elementId: element.id,
                            hover: true,
                            overlayId,
                        });
                    });
                }
            }
        );
        bpmnManager.eventBus!.on(
            "element.out",
            ({ element }: Event & { element: BpmnElement }) => {
                // Set overlays for current element to hover false.
                if (element.id in overlaysListeningToState.value) {
                    Object.entries(
                        overlaysListeningToState.value[element.id]
                    ).forEach(([overlayId, definition]) => {
                        definition.onStateChange?.({
                            elementId: element.id,
                            hover: false,
                            overlayId,
                        });
                    });
                }
            }
        );

        bpmnManager.eventBus!.on("canvas.destroy", () => {
            for (const id in htmls) {
                render(null, htmls[id]);
                delete htmls[id];
            }
        });
    };

    watch(
        [
            bpmnManager.initialized,
            processElements,
            () => features?.roleSelect?.enabled.value,
            () => features?.navigationalElements?.enabled.value,
            () => features?.linkedEntities?.enabled.value,
            () => features?.compliancy?.enabled?.value,
            () => features?.documents?.enabled?.value,
            () => features?.description?.enabled?.value,
        ],
        () => {
            if (!bpmnManager.initialized.value) {
                return;
            }
            update();
        }
    );

    const highlight = (elementId: string) => {
        _currentHighlightId = elementId;
        applyHighlight(elementId);
    };

    const clearHighlight = (elementId: string) => {
        if (_currentHighlightId === elementId) {
            _currentHighlightId = null;
        }
        removeHighlight(elementId);
    };

    onMounted(() => {
        isMounted.value = true;
    });

    onUnmounted(() => {
        isMounted.value = false;
    });

    const userToggableLayers = computed(() => {
        const definitions = getLayerDefinitions();
        return definitions
            .filter((d) => d.userToggable)
            .map((d) => ({
                type: d.type,
                label: d.label,
                color: d.color ?? "#666666",
                icon: d.icon,
                visible: layerVisibility.value[d.type] ?? true,
            }));
    });

    return {
        attach,
        getHtml,
        setLayerVisibility,
        highlight,
        clearHighlight,
        userToggableLayers,
    };
}
